/**
 * Works out what to hand <nuxt-img> for a given call site — Asana O2 image audit.
 *
 * THE CRITICAL DETAIL: `width`, `height`, `quality`, `format` and `fit` must be
 * passed as PROPS on <nuxt-img>, never inside its `modifiers` object. Its
 * useImageProps() builds the final modifier set as
 *
 *   { ...props.modifiers, width: props.width, height: props.height,
 *     format: props.format, quality: props.quality || config.quality,
 *     fit: props.fit }
 *
 * so those five keys inside `modifiers` are unconditionally overwritten by the
 * same-named props — with `undefined` when the prop is unset, which the
 * provider's operations generator then drops. Put them in `modifiers` and they
 * silently vanish from the URL.
 *
 * That is why the previous version appeared to work: it sent raw `w`, `q` and
 * `fm`, which are not prop names, so they survived as passthrough. Two of the
 * three were then ignored by Cloudflare, which reads `w` but not `fm`:
 *
 *   - `fm: 'webp'` was inert, so format negotiation never happened and every
 *     image came back in its source format. `f=auto` takes a 600px site photo
 *     from 63KB of JPEG to 28KB of AVIF.
 *   - `fit: 'max'` is not a Cloudflare fit value; that URL is a 400.
 *   - `h: null` emitted a valueless `h`, since the generator skips `undefined`
 *     but not `null`.
 *
 * Anything left in `modifiers` here is a key nuxt-img has no prop for, and so
 * reaches Cloudflare untouched.
 */

export const MAX_WIDTH = 1920;

export interface ImageModifierInput {
    width?: number | string | boolean | null;
    height?: number | string | boolean | null;
    quality?: number;
    /** Screen-keyed, e.g. "xs:100vw md:50vw". See SIZES_FULL_BLEED. */
    sizes?: string;
    /** Per-call-site passthrough, applied last. */
    mod?: Record<string, unknown>;
}

export interface ImageBindings {
    /** Spread onto <nuxt-img> as props. */
    width?: number;
    height?: number;
    quality: number;
    format: string;
    fit: string;
    /** Bound to the `modifiers` prop. */
    modifiers: Record<string, unknown>;
}

const toPositiveInt = (value: unknown): number | null => {
    const n = parseInt(String(value), 10);
    return Number.isFinite(n) && n > 0 ? n : null;
};

/**
 * `sizes` is parsed as `screen:size` pairs — an entry with no colon is filed
 * under a literal "1px" screen, which is how a plain "100vw" ended up
 * requesting a ONE PIXEL WIDE image. Keys must match nuxt.config's `screens`.
 */
export const SIZES_FULL_BLEED = 'xs:100vw sm:100vw md:100vw lg:100vw xl:100vw xxl:100vw';

/**
 * Site-card image slots, keyed by the card's `type`. The numbers are the slots
 * measured in a browser against staging, not guesses:
 *
 *   type    390    768    1024   1280   1440
 *   large   358    368    496    559    559     (event page; homepage is smaller)
 *   list     70    169    233    273    273
 *   map      70     70     70     70     70     (fixed 70x70 thumbnail)
 *
 * These call sites previously hardcoded `width=300`, which was wrong in both
 * directions at once: the 70px list thumbnail pulled a 300px asset, while the
 * large card on an event page displayed a 300px asset in a 559px slot — 0.54
 * coverage at 1x, half that on a 2x screen, which is the softness reported in
 * Asana O2-79. Quality was only half of that ticket; this is the other half.
 *
 * `large` is expressed in vw rather than px because the same card renders at
 * two quite different widths depending on its container (559 on an event page
 * against 326 on the homepage at the same viewport), and a single px ladder
 * cannot satisfy both. 50vw is the upper bound of what it ever occupies from
 * $tablet up, so it always covers the slot; the cost is over-fetching on the
 * narrower homepage instance. Correct-but-generous beats visibly blurry, and
 * `f=auto` (AVIF/WebP) keeps the extra bytes modest.
 *
 * `list` and `map` are fixed-width slots, so they get exact px ladders and
 * fetch nothing they cannot use.
 */
export const SIZES_SITE_CARD: Record<string, string> = {
    large: 'xs:100vw sm:100vw md:50vw lg:50vw xl:50vw xxl:50vw',
    list: 'xs:70px sm:70px md:169px lg:233px xl:273px xxl:273px',
    map: 'xs:70px sm:70px md:70px lg:70px xl:70px xxl:70px',
};

/**
 * The homepage Sites carousel, which is the OTHER instance of the large card
 * the note above describes — and the one 50vw over-serves worst.
 *
 * Measured on the homepage carousel:
 *
 *   vw     320-639   640-767   768   1024   1280   1440+
 *   card    0.8vw     0.8vw    208    281    326    326
 *
 * The mobile figure is exact: the strip carries `padding-right: 20%`, so the
 * card is 80% of a container already inset 16px each side. From $tablet up the
 * card is a fraction of a container capped at $hd, so it stops growing at 326
 * and never reaches 50vw again.
 *
 * At 1384 that gap is the whole bug: 50vw asked for 692px to fill a 326px slot,
 * so the browser fetched w=720 at 1x and would have taken ~1440 on a 2x screen.
 * The ladder below asks for the slot itself, which lets the 2x candidate land
 * around 720 instead — a real 2x image for the card, rather than a 4x one.
 *
 * Each entry is the widest the card gets anywhere in that band, rounded up, so
 * no viewport inside a band is ever served short and upscaled.
 */
export const SIZES_SITE_CARD_CAROUSEL = 'xs:80vw sm:80vw md:285px lg:330px xl:330px xxl:330px';

/** Neighborhood squares, measured the same way: 171 / 169 / 233 / 273 / 273. */
export const SIZES_NEIGHBORHOOD_SQUARE = 'xs:180px sm:180px md:180px lg:240px xl:280px xxl:280px';

export function buildImageBindings(input: ImageModifierInput = {}): ImageBindings {
    const responsive = Boolean(input.sizes);

    let width = toPositiveInt(input.width);
    if (width && width > MAX_WIDTH) width = MAX_WIDTH;

    const height = toPositiveInt(input.height);

    const modifiers: Record<string, unknown> = { ...(input.mod ?? {}) };

    // A call site that gives us neither a width nor `sizes` still needs a bound,
    // or Cloudflare returns the untouched original — around 400KB for a site
    // photo. Applied as the raw `w` passthrough rather than the width prop, so
    // it does not also stamp a misleading intrinsic width attribute on markup
    // whose real size we do not know. A floor, not a target.
    if (!width && !responsive && modifiers.w === undefined) {
        modifiers.w = MAX_WIDTH;
    }

    return {
        // Omitted under `sizes`, which makes nuxt-img derive a width per screen.
        ...(responsive || !width ? {} : { width }),
        ...(height ? { height } : {}),
        quality: input.quality ?? 90,
        format: 'auto',
        // Cloudflare's "fit within these bounds, never upscale" — what `max` meant.
        fit: 'scale-down',
        modifiers,
    };
}
